import { useRef, useState, useCallback } from "react";
import { ChevronsLeftRight } from "lucide-react";

/* Interactive Before/After slider — drag the handle (mouse, touch, keyboard) */
export const BeforeAfterSlider = ({ before, after, alt, labels, testid }) => {
  const ref = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);

  const move = useCallback((clientX) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    setPos(Math.min(96, Math.max(4, ((clientX - r.left) / r.width) * 100)));
  }, []);

  const onKey = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(4, p - 5));
    if (e.key === "ArrowRight") setPos((p) => Math.min(96, p + 5));
  };

  return (
    <div
      ref={ref}
      role="slider"
      tabIndex={0}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pos)}
      aria-label={alt}
      data-testid={testid}
      onKeyDown={onKey}
      onPointerDown={(e) => {
        dragging.current = true;
        e.currentTarget.setPointerCapture(e.pointerId);
        move(e.clientX);
      }}
      onPointerMove={(e) => dragging.current && move(e.clientX)}
      onPointerUp={() => (dragging.current = false)}
      onPointerCancel={() => (dragging.current = false)}
      className="group relative aspect-[4/5] w-full cursor-ew-resize touch-none select-none overflow-hidden rounded-3xl ring-1 ring-blush transition-shadow hover:ring-rose/50 card-lift"
    >
      <img src={after} alt={`${alt} — depois`} className="absolute inset-0 h-full w-full object-cover" draggable={false} loading="lazy" />
      <div className="absolute inset-0 overflow-hidden" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}>
        <img src={before} alt={`${alt} — antes`} className="absolute inset-0 h-full w-full object-cover" draggable={false} loading="lazy" />
      </div>

      <span className="absolute left-3 top-3 rounded-full bg-ink/60 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white backdrop-blur-sm" data-testid={`${testid}-label-before`}>
        {labels.before}
      </span>
      <span className="absolute right-3 top-3 rounded-full bg-rose/85 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white backdrop-blur-sm" data-testid={`${testid}-label-after`}>
        {labels.after}
      </span>

      <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -left-px w-0.5 bg-white/90 shadow-[0_0_12px_rgba(0,0,0,0.35)]" />
        <div className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 flex h-11 w-11 items-center justify-center rounded-full bg-white text-rosedeep shadow-[0_8px_25px_rgba(43,27,32,0.4)] ring-2 ring-rose/60 transition-transform duration-300 group-hover:scale-110" data-testid={`${testid}-handle`}>
          <ChevronsLeftRight className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
};

export default BeforeAfterSlider;
